/**
 * ProgramsTeaser.jsx — Slim homepage teaser linking to /programs/pilots.
 *
 * Single-row band. Lists the 4 pilot packs as compact chips with a "Take me there"
 * CTA. Designed to be ~80px tall so the homepage stays uncluttered.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Workflow, TrendingUp, Target } from 'lucide-react';

const SCARLET = '#CD2200';
const INK = '#0A0A0A';
const MUTED = '#6B6B6B';
const HAIR = 'rgba(15,23,42,0.06)';

const PACKS = [
  { id: 'csr_foundation',         label: 'CSR & Foundations',  icon: ShieldCheck, accent: '#CD2200' },
  { id: 'trade_corridor',         label: 'Trade Corridor',     icon: Workflow,    accent: '#2563EB' },
  { id: 'enterprise_finance',     label: 'Enterprise Finance', icon: TrendingUp,  accent: '#10B981' },
  { id: 'pos_misuse_prevention',  label: 'POS Misuse',         icon: Target,      accent: '#D97706' },
];

export default function ProgramsTeaser() {
  return (
    <section
      className="w-full"
      style={{ background: 'var(--g-bg-page, #FAFAFA)' }}
      data-testid="programs-teaser-section"
    >
      <div className="max-w-[960px] mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="rounded-xl bg-white"
          style={{ border: `1px solid ${HAIR}` }}
        >
          <div className="px-6 py-5 sm:px-8 sm:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            {/* Left — eyebrow + headline */}
            <div className="flex-1 min-w-0">
              <div
                className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                style={{ color: SCARLET }}
              >
                Programs · Deployable
              </div>
              <div
                className="text-[18px] sm:text-[20px] leading-snug font-semibold"
                style={{ color: INK, fontFamily: '"Inter Tight", sans-serif', letterSpacing: '-0.01em' }}
              >
                Four pilot packs ready for the world.
              </div>
              <div className="text-[12.5px] mt-1.5" style={{ color: MUTED }}>
                KPI baselines · proof requirements · 30-day goals · auto-flywheel ingestion.
              </div>

              {/* Chips */}
              <div className="mt-4 flex flex-wrap gap-1.5">
                {PACKS.map(({ id, label, icon: Icon, accent }) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-mono"
                    style={{
                      color: '#3D3D3D',
                      background: `${accent}0D`,
                      border: `1px solid ${accent}33`,
                    }}
                    data-testid={`programs-teaser-chip-${id}`}
                    data-lens-id={`pilot-pack-${id}`}
                    data-lens-type="program"
                    data-lens-title={`${label} Pilot Pack`}
                    data-lens-meaning={`Deployable pilot configuration for ${label.toLowerCase()} — KPI baselines, proof requirements, 30-day goals, auto-flywheel ingestion.`}
                    data-lens-actions={`Open pack detail|Activate pilot workspace|Review KPIs`}
                    data-lens-state="ready"
                  >
                    <Icon size={12} style={{ color: accent }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — CTA */}
            <div className="shrink-0 flex sm:items-end">
              <Link
                to="/programs/pilots"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12.5px] font-medium transition-colors"
                style={{
                  background: INK,
                  color: 'white',
                  fontFamily: '"Inter Tight", sans-serif',
                }}
                data-testid="programs-teaser-cta"
              >
                Explore Programs
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
